import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBookOpen,
  FaCalendarCheck,
  FaClipboardCheck,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaGraduationCap,
  FaGlobe,
  FaSchool,
  FaUsers,
} from "react-icons/fa";
import { useTenant } from "../context/TenantContext";
import Seo from "./Seo";

const shortcutCards = [
  {
    title: "Teachers",
    description: "Manage teacher profiles, assignments, and availability.",
    path: "/admin/teachers",
    dashboardPath: "/admin/teachers",
    icon: FaGraduationCap,
    tone: "bg-blue-50 text-blue-700",
  },
  {
    title: "Students",
    description: "Review student records, classes, guardians, and status.",
    path: "/admin/students",
    dashboardPath: "/admin/students",
    icon: FaUsers,
    tone: "bg-emerald-50 text-emerald-700",
  },
  {
    title: "Schools/Tenants",
    description: "Open school context and manage tenant operations.",
    path: "/admin/users",
    dashboardPath: "/admin/users",
    icon: FaSchool,
    tone: "bg-cyan-50 text-cyan-700",
  },
  {
    title: "Admissions",
    description: "Review applications and keep admission workflows moving.",
    path: "/admin/admissions",
    dashboardPath: "/admin/admissions",
    icon: FaClipboardList,
    tone: "bg-amber-50 text-amber-700",
  },
  {
    title: "Attendance",
    description: "Open attendance and student activity tools.",
    path: "/admin/students-activity",
    dashboardPath: "/admin/students-activity",
    icon: FaCalendarCheck,
    tone: "bg-violet-50 text-violet-700",
  },
  {
    title: "Assessments/Exams",
    description: "Create assessments and review academic evaluation tools.",
    path: "/admin/exams",
    dashboardPath: "/admin/assessments",
    icon: FaClipboardCheck,
    tone: "bg-rose-50 text-rose-700",
  },
  {
    title: "Fees/Payments",
    description: "Track invoices, collections, dues, and payment status.",
    path: "/admin/fees",
    dashboardPath: "/admin/fees",
    icon: FaFileInvoiceDollar,
    tone: "bg-orange-50 text-orange-700",
  },
  {
    title: "Reports",
    description: "Review activity insights and operational reporting tools.",
    path: "/admin/students-activity",
    dashboardPath: "/admin/students-activity",
    icon: FaBookOpen,
    tone: "bg-slate-100 text-slate-700",
  },
  {
    title: "School Website",
    description: "Customize homepage banner sliders, achievements, and events.",
    path: "/admin/home-slider",
    dashboardPath: "/admin/home-slider",
    icon: FaGlobe,
    tone: "bg-teal-50 text-teal-700",
  },
];

const quickActions = [
  {
    title: "Open Dashboard",
    description: "Continue with detailed admin workflows.",
    path: "/admin-board",
  },
  {
    title: "School Website & Slider",
    description: "Customize public homepage sliders, achievements, and events.",
    path: "/admin/home-slider",
    dashboardPath: "/admin/home-slider",
  },
  {
    title: "Add a student",
    description: "Open the student management workspace.",
    path: "/admin/students",
    dashboardPath: "/admin/students",
  },
  {
    title: "Review admissions",
    description: "Check applications that need attention.",
    path: "/admin/admissions",
    dashboardPath: "/admin/admissions",
  },
];

const getDisplayName = () => {
  const storedName = localStorage.getItem("currentUserName") || "";
  const normalizedName = storedName
    .split("@")[0]
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .trim();

  return normalizedName || "Admin";
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  if (hour >= 17 && hour < 21) return "Good evening";
  return "Hello";
};

const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : {};
  } catch {
    return {};
  }
};

const AdminHome = () => {
  const { activeTenant } = useTenant();
  const displayName = getDisplayName();
  const storedUser = getStoredUser();
  const schoolName = activeTenant?.name || storedUser?.schoolName || "";
  const roleLabel = storedUser?.role || "Admin";

  return (
    <div className="min-h-full bg-slate-50 text-slate-900">
      <Seo
        title="Admin Home | ClassNX"
        description="ClassNX admin workspace overview."
        canonical="/admin-home"
        robots="noindex, follow"
      />

      <div className="h-full min-h-0 w-full py-0">
        <main className="min-w-0">
          <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600 px-6 py-8 text-white shadow-xl shadow-blue-900/15 sm:px-8 sm:py-10 lg:px-10">
          <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full border-[26px] border-white/10" />
          <div className="absolute -bottom-24 right-24 h-48 w-48 rounded-full border-[18px] border-teal-200/10" />
          <div className="relative max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-100">ClassNX Admin Workspace</p>
            <h1 className="mt-4 text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">
              {getGreeting()}, {displayName}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-blue-50 sm:text-base">
              Welcome back to ClassNX. Get a clear view of your school operations and jump directly into the work that matters today.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-semibold text-blue-50">
              <span className="rounded-full border border-white/20 bg-white/10 px-3 py-2">{schoolName || "School workspace"}</span>
              <span className="rounded-full border border-white/20 bg-white/10 px-3 py-2">{roleLabel}</span>
              <span className="rounded-full border border-white/20 bg-white/10 px-3 py-2">Account active</span>
            </div>
          </div>
          </section>

          <section className="mt-8" aria-labelledby="quick-actions-heading">
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Move faster</p>
              <h2 id="quick-actions-heading" className="mt-2 text-2xl font-black text-slate-900">Quick Actions</h2>
            </div>
            <p className="text-sm text-slate-500">Your most common starting points.</p>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {quickActions.map((action) => (
              <Link
                key={action.path}
                to="/admin-board"
                state={{ adminModulePath: action.dashboardPath || action.path }}
                className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-slate-900">{action.title}</h3>
                    <p className="mt-2 text-sm leading-5 text-slate-500">{action.description}</p>
                  </div>
                  <FaArrowRight className="mt-1 shrink-0 text-sm text-blue-600 transition group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
          </section>

          <section className="mt-10" aria-labelledby="shortcuts-heading">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Admin modules</p>
            <h2 id="shortcuts-heading" className="mt-2 text-2xl font-black text-slate-900">Everything in one place</h2>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {shortcutCards.map((card) => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.title}
                  to="/admin-board"
                  state={{ adminModulePath: card.dashboardPath || card.path }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md"
                >
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${card.tone}`}>
                    <Icon />
                  </span>
                  <h3 className="mt-4 font-bold text-slate-900 group-hover:text-blue-700">{card.title}</h3>
                  <p className="mt-2 min-h-10 text-sm leading-5 text-slate-500">{card.description}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-blue-700">
                    Open module <FaArrowRight className="transition group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
          </section>

          <section className="mt-10 grid gap-5 lg:grid-cols-[1.35fr_0.65fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
            <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Live workspace feed</p>
                <h2 className="mt-2 text-2xl font-black text-slate-900">Recent Activity</h2>
              </div>
              <span className="inline-flex w-fit rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500">No new updates</span>
            </div>
            <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
              <p className="font-semibold text-slate-700">Your activity feed is clear</p>
              <p className="mx-auto mt-2 max-w-md text-sm leading-5 text-slate-500">
                Recent admin events will appear here when activity data is available.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Workspace status</p>
            <h2 className="mt-2 text-2xl font-black text-slate-900">Ready to work</h2>
            <dl className="mt-6 space-y-4">
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 text-sm">
                <dt className="text-slate-500">Session</dt>
                <dd className="font-bold text-emerald-600">Active</dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 text-sm">
                <dt className="text-slate-500">Workspace</dt>
                <dd className="max-w-[11rem] truncate text-right font-bold text-slate-800">{schoolName || "Not specified"}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 text-sm">
                <dt className="text-slate-500">Live metrics</dt>
                <dd className="font-bold text-slate-500">Unavailable</dd>
              </div>
            </dl>
            <p className="mt-5 text-xs leading-5 text-slate-400">
              Operational metrics will appear here when the connected admin data source provides them.
            </p>
          </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default AdminHome;
